import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FileUp,
  FileText,
  Loader2,
  Download,
  Eye,
  Scissors,
  Merge,
  AlertTriangle,
  Check,
  X,
} from "lucide-react";
import { useProjectContext } from "../../../../hooks/useProjectContext";
import {
  pdfSplitterAPI,
  uploadSplitterPDF,
} from "../../../../services/pdfSplitterAPI";
import PdfPreviewDrawer from "./PdfPreviewDrawer";

const formatSize = (bytes) =>
  bytes == null
    ? "—"
    : bytes >= 1048576
      ? `${(bytes / 1048576).toFixed(1)} MB`
      : `${Math.ceil(bytes / 1024)} KB`;
const errorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string"
    ? detail
    : err?.message || "Something went wrong";
};
const readable = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const isBusy = (s) =>
  ["pending_upload", "queued", "analyzing", "generating"].includes(s);
const copyPlan = (docs) =>
  docs.map(
    ({
      filename,
      start_page,
      end_page,
      document_type,
      reference_number,
      confidence,
      requires_review,
    }) => ({
      filename,
      start_page,
      end_page,
      document_type: document_type || "Other",
      reference_number: reference_number || null,
      confidence: confidence == null ? null : Number(confidence),
      requires_review: !!requires_review,
    }),
  );

export default function PdfSplitterView() {
  const { activeProject } = useProjectContext();
  const projectId = activeProject?.id;
  const [file, setFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [plan, setPlan] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const fileInput = useRef(null);
  const jobId = job?.id;

  const list = useCallback(async () => {
    if (!projectId) return;
    try {
      setJobs((await pdfSplitterAPI.list(projectId)).jobs);
    } catch (_) {
      /* list is secondary */
    }
  }, [projectId]);

  useEffect(() => {
    setJob(null);
    setPlan([]);
    setFile(null);
    setError("");
    list();
  }, [projectId, list]);
  useEffect(() => {
    if (
      !jobId ||
      !projectId ||
      !isBusy(job?.status) ||
      job.status === "pending_upload"
    )
      return undefined;
    let mounted = true;
    const poll = async () => {
      try {
        const next = await pdfSplitterAPI.get(projectId, jobId);
        if (!mounted) return;
        setJob(next);
        if (
          next.status === "ready_for_review" ||
          next.status === "completed" ||
          next.status === "failed"
        )
          list();
      } catch (err) {
        if (mounted) setError(errorMessage(err));
      }
    };
    poll();
    const handle = setInterval(poll, 3500);
    return () => {
      mounted = false;
      clearInterval(handle);
    };
  }, [jobId, job?.status, projectId, list]);
  useEffect(() => {
    if (job?.status === "ready_for_review" && !dirty)
      setPlan(
        copyPlan(
          job.final_plan?.length ? job.final_plan : job.proposed_plan || [],
        ),
      );
  }, [
    job?.status,
    job?.plan_revision,
    job?.proposed_plan,
    job?.final_plan,
    dirty,
  ]);

  const begin = async (event) => {
    event.preventDefault();
    if (!file || !projectId || uploadBusy) return;
    setUploadBusy(true);
    setUploadPct(0);
    setError("");
    setJob(null);
    setDirty(false);
    setPlan([]);
    try {
      setJob(await uploadSplitterPDF(projectId, file, setUploadPct));
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      await list();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploadBusy(false);
    }
  };
  const openJob = async (id) => {
    if (dirty && !window.confirm("Discard unsaved boundary changes?")) return;
    setError("");
    setDirty(false);
    setPlan([]);
    setPreview(null);
    try {
      setJob(await pdfSplitterAPI.get(projectId, id));
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const change = (fn) => {
    setPlan((prev) => fn(copyPlan(prev)));
    setDirty(true);
    setError("");
  };
  const rename = (index, value) =>
    change((docs) =>
      docs.map((d, i) =>
        i === index ? { ...d, filename: value, requires_review: false } : d,
      ),
    );
  const changeEnd = (index, raw) => {
    const value = Number(raw);
    change((docs) => {
      const next = docs[index + 1];
      if (
        !Number.isInteger(value) ||
        value < docs[index].start_page ||
        (next && value >= next.end_page) ||
        (!next && value !== job.source_page_count)
      )
        return docs;
      docs[index].end_page = value;
      docs[index].requires_review = false;
      if (next) {
        next.start_page = value + 1;
        next.requires_review = false;
      }
      return docs;
    });
  };
  const changeStart = (index, raw) => {
    const value = Number(raw);
    change((docs) => {
      if (
        index === 0 ||
        !Number.isInteger(value) ||
        value <= docs[index - 1].start_page ||
        value > docs[index].end_page
      )
        return docs;
      docs[index - 1].end_page = value - 1;
      docs[index - 1].requires_review = false;
      docs[index].start_page = value;
      docs[index].requires_review = false;
      return docs;
    });
  };
  const split = (index) =>
    change((docs) => {
      const item = docs[index];
      if (item.start_page === item.end_page) return docs;
      const boundary = Math.floor((item.start_page + item.end_page) / 2);
      const originalEnd = item.end_page;
      item.end_page = boundary;
      item.requires_review = false;
      docs.splice(index + 1, 0, {
        ...item,
        start_page: boundary + 1,
        end_page: originalEnd,
        filename: `Document_${boundary + 1}.pdf`,
        confidence: null,
        requires_review: false,
      });
      return docs;
    });
  const merge = (index) =>
    change((docs) => {
      if (index >= docs.length - 1) return docs;
      docs[index].end_page = docs[index + 1].end_page;
      docs[index].requires_review = false;
      docs.splice(index + 1, 1);
      return docs;
    });
  const accept = (index) =>
    change((docs) => {
      docs[index].requires_review = false;
      return docs;
    });
  const runAction = async (action) => {
    setSaving(true);
    setError("");
    try {
      const next =
        action === "generate"
          ? await pdfSplitterAPI.generate(
              projectId,
              job.id,
              job.plan_revision,
              plan,
            )
          : await pdfSplitterAPI.savePlan(
              projectId,
              job.id,
              job.plan_revision,
              plan,
            );
      setDirty(false);
      setJob(next);
      await list();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const previewOriginal = async (item) => {
    try {
      const { url } = await pdfSplitterAPI.sourceUrl(projectId, job.id);
      setPreview({
        title: item.filename,
        url,
        startPage: item.start_page,
        endPage: item.end_page,
      });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const openOutput = async (output, download = false) => {
    try {
      const { url } = await pdfSplitterAPI.outputUrl(
        projectId,
        job.id,
        output.id,
        download,
      );
      if (download) window.location.assign(url);
      else
        setPreview({
          title: output.filename,
          url,
          startPage: 1,
          endPage: output.end_page - output.start_page + 1,
        });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const downloadZip = async () => {
    try {
      window.location.assign(
        (await pdfSplitterAPI.zipUrl(projectId, job.id)).url,
      );
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const pageTotal = useMemo(
    () => plan.reduce((sum, d) => sum + d.end_page - d.start_page + 1, 0),
    [plan],
  );
  const activeStatus = job?.status;

  return (
    <div className="space-y-4 text-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">AI Assisted PDF Splitter</h2>
          <p className="text-xs text-gray-500">
            Original documents are preserved.
          </p>
        </div>
        {job && (
          <button
            type="button"
            className="rounded border px-3 py-1.5 text-xs hover:bg-gray-50"
            onClick={() => {
              setJob(null);
              setDirty(false);
              setPlan([]);
              setError("");
            }}
          >
            New split
          </button>
        )}
      </div>
      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}
      {(!job || activeStatus === "failed" || activeStatus === "cancelled") && (
        <form onSubmit={begin} className="rounded-lg border bg-gray-50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <FileUp size={18} className="text-orange-500" />
            <span className="text-sm font-medium">1 · Upload & Analyze</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInput}
              type="file"
              accept=".pdf,application/pdf"
              disabled={uploadBusy}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="max-w-full flex-1 text-sm"
            />
            <button
              type="submit"
              disabled={!file || uploadBusy}
              className="rounded bg-orange-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {uploadBusy ? "Uploading…" : "Upload & Analyze"}
            </button>
          </div>
          {file && (
            <p className="mt-2 text-xs text-gray-500">
              {file.name} · {formatSize(file.size)}
            </p>
          )}
          {uploadBusy && (
            <div className="mt-3">
              <div className="mb-1 text-xs">
                Uploading directly to S3 · {uploadPct}%
              </div>
              <progress className="h-2 w-full" max="100" value={uploadPct} />
            </div>
          )}
        </form>
      )}
      {job && (
        <div className="rounded-lg border bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">
                {job.source_filename}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {formatSize(job.source_size_bytes)} ·{" "}
                {job.source_page_count
                  ? `${job.source_page_count} pages`
                  : "Page count pending"}{" "}
                · {readable(activeStatus)}
              </p>
            </div>
            {isBusy(activeStatus) && (
              <Loader2 className="animate-spin text-orange-500" size={20} />
            )}
          </div>
          {["queued", "analyzing", "generating"].includes(activeStatus) && (
            <div className="mt-3">
              <p className="mb-1 text-xs">
                {activeStatus === "generating"
                  ? "Generating PDFs & ZIP"
                  : "AI analysis"}{" "}
                ·{" "}
                {activeStatus === "generating"
                  ? job.generation_progress_pct
                  : job.analysis_progress_pct}
                %
              </p>
              <progress
                className="h-2 w-full"
                max="100"
                value={
                  activeStatus === "generating"
                    ? job.generation_progress_pct
                    : job.analysis_progress_pct
                }
              />
            </div>
          )}
          {activeStatus === "failed" && (
            <p className="mt-2 text-sm text-red-700">
              {job.error_message || "Processing failed"}
            </p>
          )}
          {(activeStatus === "queued" ||
            activeStatus === "ready_for_review") && (
            <button
              className="mt-2 text-xs text-gray-500 underline"
              onClick={async () => {
                try {
                  await pdfSplitterAPI.cancel(projectId, job.id);
                  setJob(await pdfSplitterAPI.get(projectId, job.id));
                  await list();
                } catch (err) {
                  setError(errorMessage(err));
                }
              }}
            >
              Cancel job
            </button>
          )}
        </div>
      )}
      {job?.status === "ready_for_review" && (
        <section className="space-y-3 rounded-lg border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">2 · Review & Save</h3>
              <p className="mt-1 text-xs text-gray-500">
                Adjust page ranges, split/merge, rename, then generate output
                files.
              </p>
            </div>
            <div className="text-xs text-gray-500">
              {plan.length} documents · {pageTotal}/{job.source_page_count}{" "}
              pages
            </div>
          </div>
          {job.review_warnings?.map((w, i) => (
            <p key={i} className="flex gap-1.5 text-xs text-amber-700">
              <AlertTriangle size={15} />
              {w}
            </p>
          ))}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-left text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2">Filename</th>
                  <th className="p-2">Detected type</th>
                  <th className="p-2">Start</th>
                  <th className="p-2">End</th>
                  <th className="p-2">Pages</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((d, i) => (
                  <tr
                    key={`${d.start_page}-${i}`}
                    className="border-t align-middle hover:bg-gray-50"
                  >
                    <td className="p-2">
                      <input
                        aria-label={`Filename ${i + 1}`}
                        value={d.filename}
                        onChange={(e) => rename(i, e.target.value)}
                        className="w-48 rounded border px-2 py-1.5"
                      />
                      {d.requires_review && (
                        <span className="mt-1 block text-amber-700">
                          Needs review
                        </span>
                      )}
                    </td>
                    <td className="p-2">
                      <select
                        aria-label={`Document type ${i + 1}`}
                        value={d.document_type}
                        onChange={(e) =>
                          change((docs) => {
                            docs[i].document_type = e.target.value;
                            return docs;
                          })
                        }
                        className="w-28 rounded border p-1.5"
                      >
                        {[
                          ...new Set([
                            "RFI",
                            "Submittal",
                            "Test Report",
                            "Inspection",
                            "NCR",
                            "Other",
                            d.document_type,
                          ]),
                        ].map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <input
                        aria-label={`Start page ${i + 1}`}
                        type="number"
                        value={d.start_page}
                        min={i ? plan[i - 1].start_page + 1 : 1}
                        max={d.end_page}
                        disabled={i === 0}
                        onChange={(e) => changeStart(i, e.target.value)}
                        className="w-16 rounded border px-1 py-1.5 disabled:opacity-50"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        aria-label={`End page ${i + 1}`}
                        type="number"
                        value={d.end_page}
                        min={d.start_page}
                        max={
                          i < plan.length - 1
                            ? plan[i + 1].end_page - 1
                            : job.source_page_count
                        }
                        disabled={i === plan.length - 1}
                        onChange={(e) => changeEnd(i, e.target.value)}
                        className="w-16 rounded border px-1 py-1.5 disabled:opacity-50"
                      />
                    </td>
                    <td className="p-2">{d.end_page - d.start_page + 1}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <button
                          aria-label="Preview"
                          title="Preview pages"
                          onClick={() => previewOriginal(d)}
                        >
                          <Eye size={17} />
                        </button>
                        <button
                          aria-label="Split"
                          title="Split at midpoint"
                          disabled={d.start_page === d.end_page}
                          onClick={() => split(i)}
                          className="disabled:opacity-30"
                        >
                          <Scissors size={17} />
                        </button>
                        <button
                          aria-label="Merge next"
                          title="Merge with next"
                          disabled={i === plan.length - 1}
                          onClick={() => merge(i)}
                          className="disabled:opacity-30"
                        >
                          <Merge size={17} />
                        </button>
                        {d.requires_review && (
                          <button
                            aria-label="Accept"
                            title="Accept suggestion"
                            onClick={() => accept(i)}
                          >
                            <Check size={17} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {plan.some((d) => d.requires_review) && (
            <p className="text-xs text-amber-700">
              Accept or correct documents marked Needs Review before generating.
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              disabled={saving || !dirty}
              onClick={() => runAction("save")}
              className="rounded border px-3 py-2 text-xs disabled:opacity-40"
            >
              Save Draft Plan
            </button>
            <button
              type="button"
              disabled={
                saving ||
                plan.length === 0 ||
                pageTotal !== job.source_page_count ||
                plan.some((d) => d.requires_review)
              }
              onClick={() => runAction("generate")}
              className="rounded bg-orange-500 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save All & Generate ZIP"}
            </button>
          </div>
        </section>
      )}
      {job?.status === "completed" && (
        <section className="space-y-3 rounded-lg border p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold">Generated PDFs</h3>
              <p className="text-xs text-gray-500">
                {job.output_count} files ·{" "}
                {formatSize(job.outputs_total_size_bytes)} combined vs{" "}
                {formatSize(job.source_size_bytes)} original · ZIP{" "}
                {formatSize(job.zip_size_bytes)}
              </p>
            </div>
            <button
              onClick={downloadZip}
              className="flex items-center gap-2 rounded bg-orange-500 px-3 py-2 text-xs text-white"
            >
              <Download size={15} />
              Download ZIP
            </button>
          </div>
          {job.outputs?.map((output) => (
            <div
              key={output.id}
              className="flex items-center gap-3 border-t py-2 text-xs"
            >
              <FileText size={16} className="shrink-0 text-gray-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{output.filename}</p>
                <p className="text-gray-500">
                  Pages {output.start_page}–{output.end_page} ·{" "}
                  {formatSize(output.file_size)}
                </p>
              </div>
              <button
                onClick={() => openOutput(output)}
                className="flex items-center gap-1 text-blue-700"
              >
                <Eye size={15} />
                Preview
              </button>
              <button
                onClick={() => openOutput(output, true)}
                className="flex items-center gap-1 text-blue-700"
              >
                <Download size={15} />
                Download
              </button>
            </div>
          ))}
        </section>
      )}
      {jobs.length > 0 && (
        <div className="rounded-lg border p-3">
          <h3 className="mb-2 text-xs font-semibold text-gray-700">
            Recent splitter jobs
          </h3>
          <div className="flex flex-wrap gap-2">
            {jobs.slice(0, 12).map((j) => (
              <button
                key={j.id}
                title={j.source_filename}
                onClick={() => openJob(j.id)}
                className={`max-w-[220px] truncate rounded border px-2 py-1.5 text-xs hover:bg-gray-50 ${j.id === jobId ? "border-orange-500" : ""}`}
              >
                {j.source_filename} · {readable(j.status)}
              </button>
            ))}
          </div>
        </div>
      )}
      {preview && (
        <PdfPreviewDrawer {...preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
